import {Injectable} from '@angular/core';
import {Observable} from "rxjs";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map} from "rxjs/operators";
import {NextVisitReminder} from "../models/next-visit-reminder.model";

/**
 * Serwis warstwy danych zarządzający systemem przypomnień o kolejnych wizytach.
 * Obsługuje harmonogramowanie notyfikacji dla pacjentów,
 * powiązanie ich z konkretnymi rezerwacjami oraz natychmiastową wysyłkę zaproszeń.
 */
@Injectable({
  providedIn: 'root'
})
export class NextVisitReminderService {

  constructor(
    private apiService: ApiService,
    public commonUtils: CommonUtils,
  ) {
  }

  /**
   * Zapisuje masowo listę zaplanowanych przypomnień.
   *
   * @param reminders Tablica obiektów przypomnień do zapisania w bazie danych.
   * @returns Observable emitujący zaktualizowaną tablicę przypomnień zwróconą przez serwer.
   */
  editReminders(reminders: NextVisitReminder[]): Observable<NextVisitReminder[]> {
    return this.apiService.post<VetineoApiResponse>('/dashboard/reminders/vt', {'nextVisitReminders': reminders}).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new NextVisitReminder(), [], vetineoApiResponse.response);
      } else {
        throw new Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Pobiera listę przypomnień powiązanych z jedną, konkretną rezerwacją.
   *
   * @param uuid Unikalny identyfikator rezerwacji.
   * @returns Observable emitujący tablicę zaplanowanych przypomnień.
   */
  getReminderForReservation(uuid: string): Observable<NextVisitReminder[]>   {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/reminders/vt/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new NextVisitReminder(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera wszystkie przypomnienia skonfigurowane dla konkretnego pacjenta
   * w ramach danej kliniki, niezależnie od tego, z jakiej wizyty wynikają.
   *
   * @param uuid Unikalny identyfikator użytkownika (właściciela/pacjenta).
   * @param vetcode Identyfikator kliniki weterynaryjnej.
   * @returns Observable emitujący tablicę przypomnień dla tego profilu.
   */
  getReminderForUser(uuid: string, vetcode: any): Observable<NextVisitReminder[]>   {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/reminders/u/${uuid}/${vetcode}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new NextVisitReminder(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wyzwala natychmiastową wysyłkę pojedynczego przypomnienia/zaproszenia (tzw. Flash Reminder),
   * ignorując standardowy harmonogram.
   *
   * @param reminderToSendNow Obiekt przypomnienia przeznaczony do natychmiastowej dystrybucji.
   * @returns Observable emitujący obiekt przypomnienia po udanej akcji wysyłki.
   */
  sendFlashReminder(reminderToSendNow: NextVisitReminder) : Observable<NextVisitReminder> {
    return this.apiService.post<VetineoApiResponse>('/dashboard/reminders/invitation',  reminderToSendNow).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new NextVisitReminder(), vetineoApiResponse.response);
      } else {
        throw new Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Wymusza przeliczenie i ponowne zaplanowanie (Reschedule) istniejących przypomnień
   * na podstawie nowych danych powiązanego obiektu lecznicy (identyfikowanego przez UUID).
   *
   * @param uuid Identyfikator lecznicy.
   * @returns Observable emitujący status 'SUCCESS' po udanej operacji.
   */
  rescheduleReminders(uuid: string) : Observable<string> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/rem/schedule/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.message) {
        return 'SUCCESS'
      } else {
        throw new Error(vetineoApiResponse.errors.main);
      }
    }));
  }
}
